import React, { useState, useEffect } from "react";
import ModalLayout from "../components/layouts/ModalLayout";
import axios from "axios";
import {
    Plus, ChevronRight, Search, Download, Upload, Edit, Trash2, Home, Shield, Users, AlertTriangle, X, FileDown, Eye,
    Bell, Clock, FileText, Archive, MapPin, CalendarDays, Copy, Map, Database, FileTextIcon, TriangleAlert, HeartHandshake, ClipboardCheck
} from 'lucide-react';
import { FaSackDollar, FaPeopleLine, FaArrowsDownToPeople, FaHandHoldingDollar } from "react-icons/fa6";
import { IoIosPeople } from "react-icons/io";
import { GenerateReportButton } from '../components/GenerateReportButton';
import { downloadEvictionReport } from '../utils/summary_report/generateReport';
import { ReportFilters } from '../types/types';
import { useAreaRiskData } from '../utils/summary_report/hooks/useAreaRiskData';
import { useInfrastructureData } from '../utils/summary_report/hooks/useInfrastructureData';
import { usePreventionData } from '../utils/summary_report/hooks/usePreventionData';
import { useDistrictGeoJson } from '../utils/summary_report/hooks/useDistrictGeoJson';
import evictionHouseholds from '../assets/report_icons/eviction-households.png';
import evictionIndividuals from '../assets/report_icons/eviction-individuals.png';
import preventionHouseholds from '../assets/report_icons/prevention-households.png';
import preventionIndividuals from '../assets/report_icons/prevention-individuals.png';
import riskDistricts from '../assets/report_icons/risk-districts.png';
import riskTotalHouseholds from '../assets/report_icons/risk-total-households.png';
import riskExtreme from '../assets/report_icons/risk-extreme.png';

type EvictionDataAggregate = {
    totalEvictionsIndividuals: number;
    totalEvictionsHouseholds: number;
    totalProtectedIndividuals: number;
    // You can extend this type with other fields if needed
};

interface HumanitarianSnapshot {
    population_in_need: number;
    population_target: number;
    population_reached: number;
    funding_required: number;
    funding_received: number;
    report_month: number;
    report_year: number;
}

interface UploadLog {
    file_name: string;
    status: 'success' | 'error';
    record_count: number;
    timestamp: string;
    upload_type: string;
    user_email: string;
}

interface Partner {
    id: number;
    title: string;
    file_url: string;
    thumbnail_url: string;
    category: string;
    created_at: string;
}

//The Data Officer Approval Module
interface ProdEviction {
    _id: number;
    _submission_time?: string;
    report_date?: string;
    region?: string;
    district?: string;
    evicted_households?: number;
    evicted_individuals?: number;
    eviction_reasons?: string;
    approved: boolean | null;
    // add more fields as needed
}

interface ApprovalLog {
    approval_id: number;
    data_review_officer: string;
    data_operation: string;
    record_count: number;
    time_period: string;
    record_ids: number[];
    created_at: string;
}

type Mode = 'bulk' | 'single' | 'history';

type EvictionsByYearPerMonth = {
    [year: string]: {
        [month: string]: {
            total_evictions_individuals: number;
            total_evictions_households: number;
        };
    };
};

const AdminPanel = () => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const date = new Date()
    const [tab, setTab] = useState<number>(1);
    const [groupedFiles, setGroupedFiles] = useState<{ [category: string]: any[] }>({});
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [showBackendModal, setShowBackendModal] = useState<boolean>(false);
    const [showFlashAlertsModal, setShowFlashAlertsModal] = useState<boolean>(false);
    const [showSnapshotModal, setShowSnapshotModal] = useState<boolean>(false);
    const [showPartnersModal, setShowPartnersModal] = useState<boolean>(false);
    const [flashAlerts, setFlashAlerts] = useState<any[]>([]); // holds all alerts
    const [reports, setReports] = useState<any[]>([]); // flat list for reuse
    const latestReports = [...reports]
        .sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())
        .slice(0, 5);
    const [file, setFile] = useState<FileList | null>(null)
    const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "success" | "error">("idle");
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [uploadSummary, setUploadSummary] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [targetModel, setTargetModel] = useState<string>('');
    const [partnerType, setPartnerType] = useState<string>('');
    const [type, setType] = useState<string>('')
    const [title, setTitle] = useState<string>('');
    const [dateField, setDateField] = useState<string>(date.toISOString().split('T')[0]); // default to current date
    const [location, setLocation] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [images, setImages] = useState<FileList | null>(null);
    const [description, setDescription] = useState<string>('');
    const [snapshot, setSnapshot] = useState<HumanitarianSnapshot | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [addedBy, setAddedBy] = useState<string>('');
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [evictionData, setEvictionData] = useState<{
        individuals: number;
        households: number;
        protected: number;
    } | null>(null);
    const [latestUploads, setLatestUploads] = useState<Record<string, any>>({});
    const [uploadLogs, setUploadLogs] = useState<UploadLog[]>([]);
    const [donorPartners, setDonorPartners] = useState<Partner[]>([]);
    const [dataPartners, setDataPartners] = useState<Partner[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    const [activeTab, setActiveTab] = useState<"donor_partners" | "data_partners">("donor_partners");
    const visiblePartners = activeTab === "donor_partners" ? donorPartners : dataPartners;

    const totalPages = Math.ceil(visiblePartners.length / itemsPerPage);
    const paginatedPartners = visiblePartners.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );
    const [reportPage, setReportPage] = useState(1);
    const reportsPerPage = 5;
    const totalReportItems = reports.length;
    const totalReportPages = Math.ceil(totalReportItems / reportsPerPage);
    const paginatedReports = reports.slice(
        (reportPage - 1) * reportsPerPage,
        reportPage * reportsPerPage
    );
    const staticDisplayConfig = {
        evictions: {
            type: 'Evictions Data',
            icon: <Home className="w-5 h-5" />,
            color: { text: '#dc2626', bg: '#fee2e2' },
        },
        preventions: {
            type: 'Preventions Data',
            icon: <Shield className="w-5 h-5" />,
            color: { text: '#16a34a', bg: '#dcfce7' },
        },
        riskmap: {
            type: 'Risk Map Data',
            icon: <Map className="w-5 h-5" />,
            color: { text: '#2563eb', bg: '#dbeafe' },
        },
    };

    const [populationInNeed, setPopulationInNeed] = useState('');
    const [populationTarget, setPopulationTarget] = useState('');
    const [populationReached, setPopulationReached] = useState('');
    const [fundingRequired, setFundingRequired] = useState('');
    const [fundingReceived, setFundingReceived] = useState('');
    const [success, setSuccess] = useState("");
    const [snapshots, setSnapshots] = useState<HumanitarianSnapshot[]>([]);
    const [loadingSnapshots, setLoadingSnapshots] = useState(true);
    const [snapshotCurrentPage, setSnapshotCurrentPage] = useState(1);
    const snapshotItemsPerPage = 5;

    const snapshotTotalItems = snapshots.length;
    const snapshotTotalPages = Math.ceil(snapshotTotalItems / snapshotItemsPerPage);

    // Slice snapshots for current page
    const paginatedSnapshots = snapshots.slice(
        (snapshotCurrentPage - 1) * snapshotItemsPerPage,
        snapshotCurrentPage * snapshotItemsPerPage
    );

    const [flashPage, setFlashPage] = useState(1);
    const alertsPerPage = 5;
    const [filter, setFilter] = useState("All Alerts");

    // Navigation handler
    const goToSnapshotPage = (page: number) => {
        if (page >= 1 && page <= snapshotTotalPages) {
            setSnapshotCurrentPage(page);
        }
    };

    // slice the displayed alerts
    // 1. Filter alerts
    const filteredAlerts =
        filter === "All Alerts"
            ? flashAlerts
            : flashAlerts.filter((a) => a.status === filter);

    // 2. Update totals
    const totalFlashItems = filteredAlerts.length;
    const totalFlashPages = Math.ceil(totalFlashItems / alertsPerPage);

    // 3. Slice per page
    const currentAlerts = filteredAlerts.slice(
        (flashPage - 1) * alertsPerPage,
        flashPage * alertsPerPage
    );

    // Data Officer Approval Module
    const [records, setRecords] = useState<ProdEviction[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [mode, setMode] = useState<Mode>('bulk');
    const [months, setMonths] = useState<string[]>([]);
    const [selectedMonth, setSelectedMonth] = useState<string>('All Months');
    const [history, setHistory] = useState<ApprovalLog[]>([]);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedHistoryIds, setSelectedHistoryIds] = useState<number[]>([]);
    const [modalRecords, setModalRecords] = useState<any[]>([]);
    const [currentApprovalLog, setCurrentApprovalLog] = useState<ApprovalLog | null>(null);
    const modeTabs: { key: Mode; label: string }[] = [
        { key: 'bulk', label: 'Bulk Update' },
        { key: 'single', label: 'Single Update' },
        { key: 'history', label: 'Approval History' },
    ];

    const [totalPendingCount, setTotalPendingCount] = useState(0);
    // Pagination
    const [recordsDrPage, setRecordsDrPage] = useState(1);
    const [historyDrPage, setHistoryDrPage] = useState(1);
    const recordsDrPerPage = 15;

    const [evictionsByYearPerMonth, setEvictionsByYearPerMonth] =
        useState<EvictionsByYearPerMonth | null>(null);

    const { riskByYearMonthDistrict } = useAreaRiskData();
    const { geojson } = useDistrictGeoJson();
    const { infrastructureByYearPerMonth, loading: infraLoading, error: infraError } = useInfrastructureData();
    const { preventionsByYearPerMonth, loading: preventionLoading, error: preventionError } = usePreventionData();

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        setUploadStatus("idle");
        setUploadError(null);

        if (!file || file.length === 0) {
            setUploadError("Please select a PDF file.");
            setUploadStatus("error");
            return;
        }

        const selectedFile = file[0];

        if (selectedFile.type !== "application/pdf") {
            setUploadError("Only PDF files are allowed.");
            setUploadStatus("error");
            return;
        }

        if (!type) {
            setUploadError("Please select a file type.");
            setUploadStatus("error");
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("type", type);

        setUploadStatus("uploading");

        try {
            const response = await fetch(`${BASE_URL}/upload-reports/`, {
                method: "POST",
                body: formData,
            });

            const result = await response.json();

            if (response.ok) {
                setUploadStatus("success");
                setFile(null);
                setType("");
                setShowAddModal(false); // Optional: auto-close
            } else {
                setUploadStatus("error");
                setUploadError(result.error || "Upload failed.");
            }
        } catch (error) {
            console.error("Upload error:", error);
            setUploadStatus("error");
            setUploadError("Something went wrong. Please try again.");
        }
    };

    const uploadFileToBackend = async (file: File | null, type: string) => {
        if (!file) {
            alert("Please select a file.");
            return;
        }

        if (!targetModel) {
            alert("Please select a model to upload to.");
            return;
        }

        const uploadType = targetModel; // ✨ ensure we're always using this for consistency

        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", uploadType); // This goes to main upload endpoint

        setIsUploading(true);
        setUploadProgress(0);
        setUploadSummary(null);

        // ✅ Extract user email from localStorage
        const userString = localStorage.getItem("e-user");
        let userEmail: string | null = null;
        if (userString && userString !== "undefined") {
            try {
                const user = JSON.parse(userString);
                userEmail = user?.user_email || null;
                console.log("🔍 Logged-in user email:", userEmail); // ✅ log it
            } catch (err) {
                console.error("Failed to parse user from localStorage", err);
            }
        }

        try {
            const response = await axios.post(
                `${BASE_URL}/upload-${uploadType}/`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                            const percentCompleted = Math.min(
                                Math.round((progressEvent.loaded * 90) / progressEvent.total),
                                90
                            );
                            setUploadProgress(percentCompleted);
                        }
                    },
                }
            );
            console.log("Upload response data:", response.data); // log the response data
            // Complete to 100% when server responds
            setUploadProgress(100);

            const message = response.data.message || "Upload completed successfully";
            const recordCountMatch = message.match(/(\d+)\s+records?/i);
            const recordCount = recordCountMatch ? parseInt(recordCountMatch[1], 10) : 0;

            setUploadSummary(message);

            // ✅ Log to UploadLog model (success)
            await axios.post(`${BASE_URL}/upload_logs/create/`, {
                user_email: userEmail, // ✅ include email
                upload_type: uploadType, // ✅ Correct key name
                status: "success",
                message: message,
                record_count: recordCount,
                file_name: file.name,
            });

            // Close modal after 2 seconds
            setTimeout(() => {
                setShowBackendModal(false);
            }, 2000);

        } catch (err: any) {
            let errorMessage = "Network or server error during upload.";

            if (err.response) {
                errorMessage =
                    err.response.data.error ||
                    err.response.data.message ||
                    `Server error: ${err.response.status}`;
            } else if (err.request) {
                errorMessage = "No response received from server";
            }

            setUploadSummary(`Error: ${errorMessage}`);
            setUploadProgress(0);

            // Log to UploadLog model (error)
            await axios.post(`${BASE_URL}/upload_logs/create/`, {
                user_email: userEmail, // ✅ include email
                upload_type: uploadType, // ✅ Correct key name
                status: "error",
                message: errorMessage,
                record_count: 0,
                file_name: file?.name || "unknown",
            });

            console.error("Upload failed:", err);
        } finally {
            setIsUploading(false);
        }
    };

    const handleFlashAlertSubmit = async (status: "Draft" | "Active") => {
        const formData = new FormData();
        formData.append("title", title);
        formData.append("date", dateField);
        formData.append("location", location);
        formData.append("description", description);
        formData.append("added_by", addedBy);
        formData.append("status", status);

        if (images) {
            for (let i = 0; i < images.length; i++) {
                formData.append("images", images[i]);
            }
        }

        try {
            const response = await fetch(`${BASE_URL}/flash-alerts/upload/`, {
                method: "POST",
                body: formData,
            });

            if (response.ok) {
                setShowFlashAlertsModal(false);
                fetchFlashAlerts(); // Refresh the list
            } else {
                console.error("Failed to submit flash alert");
            }
        } catch (error) {
            console.error("Error submitting flash alert:", error);
        }
    };

    const [uploadHistory, setUploadHistory] = useState([
        { date: '2025-06-01', filename: 'evictions_june.xlsx', status: 'Success' },
        { date: '2025-05-15', filename: 'preventions_may.xlsx', status: 'Success' },
        { date: '2025-05-28', filename: 'preventions_may.xlsx', status: 'Failed' },
    ]);

    const tabItems = [
        { id: 1, label: "Downloads", icon: Download },
        { id: 2, label: "Upload Data Files", icon: Upload },
        { id: 3, label: "Flash Alerts", icon: TriangleAlert },
        { id: 4, label: "Needs & Response", icon: HeartHandshake },
        { id: 5, label: "Pending Record Reviews", icon: ClipboardCheck, count: totalPendingCount },
    ];

    const fetchEvictionDataAggregate = async (): Promise<EvictionDataAggregate | null> => {
        try {
            const response = await fetch(`${BASE_URL}/prod_eviction_stats/`);
            if (!response.ok) throw new Error("Failed to fetch eviction data");

            const data: EvictionDataAggregate = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching eviction data:", error);
            return null;
        }
    };

    useEffect(() => {
        const fetchFiles = async () => {
            try {
                let url = `${BASE_URL}/reports/`;
                if (tab !== 1) {
                    const categoryMap: { [key: number]: string } = {
                        2: "reports",
                        3: "dashboard",
                        4: "evictionresources",
                    };
                    const selectedCategory = categoryMap[tab];
                    url += `?category=${encodeURIComponent(selectedCategory)}`;
                }

                const response = await fetch(url);
                const data = await response.json();

                // set flat list directly for use in tables
                setReports(data);

                // Group files by their category label
                const grouped = data.reduce((acc: { [key: string]: any[] }, file: any) => {
                    const cat = file.category || "Uncategorized";
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(file);
                    return acc;
                }, {});

                setGroupedFiles(grouped);
            } catch (error) {
                console.error("Failed to fetch files:", error);
            }
        };

        fetchFiles();
    }, [tab]);

    useEffect(() => {
        const loadData = async () => {
            const data = await fetchEvictionDataAggregate();
            if (data) {
                setEvictionData({
                    individuals: data.totalEvictionsIndividuals,
                    households: data.totalEvictionsHouseholds,
                    protected: data.totalProtectedIndividuals,
                });
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        const fetchLatestUploads = async () => {
            try {
                const res = await axios.get(`${BASE_URL}/upload_logs/latest_per_category/`);
                setLatestUploads(res.data);
            } catch (error) {
                console.error("Failed to fetch latest uploads:", error);
            }
        };

        fetchLatestUploads();
    }, []);

    useEffect(() => {
        const fetchUploadLogs = async () => {
            try {
                const response = await fetch(`${BASE_URL}/upload_logs/`);
                const data = await response.json();

                setUploadLogs(data.results); // ✅ Only set the actual array
            } catch (error) {
                console.error('Error fetching upload logs:', error);
            }
        };

        fetchUploadLogs();
    }, []);

    useEffect(() => {
        const userString = localStorage.getItem("e-user");

        if (userString && userString !== "undefined") {
            try {
                const user = JSON.parse(userString);
            } catch (error) {
                console.error("Error parsing user from localStorage:", error);
            }
        } else {
            console.warn("No user found in localStorage");
        }
    }, []);

    const fetchFlashAlerts = async () => {
        try {
            const response = await fetch(`${BASE_URL}/flash-alerts/`);
            if (!response.ok) throw new Error("Failed to fetch flash alerts");
            const data = await response.json();
            setFlashAlerts(data); // Store data in state
        } catch (err) {
            console.error("Error fetching flash alerts:", err);
        }
    };

    useEffect(() => {
        const fetchAllSnapshots = async () => {
            try {
                const response = await fetch(`${BASE_URL}/humanitarian-snapshots/`);
                if (!response.ok) throw new Error("Failed to fetch snapshots");

                const data = await response.json();
                setSnapshots(data);
            } catch (err) {
                console.error("Error loading snapshot history:", err);
            } finally {
                setLoadingSnapshots(false);
            }
        };
        fetchAllSnapshots();
    }, []);

    const fetchCategory = async (category: "donor_partners" | "data_partners") => {
        const res = await fetch(`${BASE_URL}/partners/?category=${category}`);
        const data = await res.json();
        if (category === "donor_partners") setDonorPartners(data);
        else setDataPartners(data);
    };

    const handleDeleteReport = async (id: number) => {
        if (!window.confirm("Are you sure you want to delete this report?")) return;

        try {
            const res = await fetch(`${BASE_URL}/reports/${id}/delete/`, {
                method: "DELETE",
            });

            if (res.ok) {
                setReports(prev => prev.filter((r) => r.id !== id));
                setGroupedFiles(prev => {
                    const updated = { ...prev };
                    for (const cat in updated) {
                        updated[cat] = updated[cat].filter(r => r.id !== id);
                    }
                    return updated;
                });
            } else {
                console.error("Failed to delete file");
            }
        } catch (err) {
            console.error("Error deleting report:", err);
        }
    };

    const uploadPartnerImage = async (file: File, partnerType: string) => {
        const formData = new FormData();
        const fileName = file.name.split(".")[0];
        const title = fileName.replace(/[-_]/g, " ");

        formData.append("title", title);
        formData.append("category", partnerType);
        formData.append("image", file);

        try {
            const response = await fetch(`${BASE_URL}/upload-partner-image/`, {
                method: "POST",
                body: formData,
            });

            const result = await response.json();
            if (response.ok) {
                return { success: true, message: result.message };
            } else {
                return { success: false, error: result.error || "Something went wrong" };
            }
        } catch (err) {
            return { success: false, error: (err as Error).message };
        }
    };

    const handleDeletePartner = async (id: number) => {
        const confirmed = window.confirm("Are you sure you want to delete this partner?");
        if (!confirmed) return;

        try {
            const res = await fetch(`${BASE_URL}/partners/${id}/delete/`, {
                method: "DELETE",
            });
            if (res.ok) {
                if (activeTab === "donor_partners") {
                    setDonorPartners((prev) => prev.filter((p) => p.id !== id));
                } else {
                    setDataPartners((prev) => prev.filter((p) => p.id !== id));
                }
            }
        } catch (err) {
            console.error("Delete failed:", err);
        }
    };

    const fetchSnapshot = async () => {
        try {
            const response = await fetch(`${BASE_URL}/latest-humanitarian-snapshot/`);
            if (!response.ok) throw new Error("Failed to fetch snapshot");

            const data = await response.json();
            setSnapshot(data);
        } catch (err: any) {
            setError(err.message || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSnapshot();
        fetchFlashAlerts();
        fetchCategory("donor_partners");
        fetchCategory("data_partners");
    }, []);

    function formatFull(num: number | string): string {
        const n = typeof num === "string" ? parseFloat(num) : num;
        return n.toLocaleString(); // Adds commas, keeps full number
    }

    const handleSnapshotSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccess("");

        try {
            if (
                !populationInNeed ||
                !populationTarget ||
                !populationReached ||
                !fundingRequired ||
                !fundingReceived
            ) {
                throw new Error("All fields are required.");
            }

            const payload = {
                population_in_need: parseInt(populationInNeed, 10),
                population_target: parseInt(populationTarget, 10),
                population_reached: parseInt(populationReached, 10),
                funding_required: parseFloat(fundingRequired),
                funding_received: parseFloat(fundingReceived),
            };

            const res = await fetch(`${BASE_URL}/submit-humanitarian-snapshot/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            const result = await res.json();

            if (!res.ok) {
                throw new Error(result.error || "Failed to submit snapshot.");
            }

            setSuccess("Snapshot submitted successfully.");
            setShowSnapshotModal(false);

            // 👉 Refresh the latest snapshot data
            fetchSnapshot();
        } catch (err: any) {
            setError(err.message || "An error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAlert = async (alertId: number) => {
        if (!window.confirm("Are you sure you want to delete this alert?")) return;

        try {
            const response = await fetch(`${BASE_URL}/flash-alerts/${alertId}/`, {
                method: "DELETE",
            });

            if (response.ok) {
                fetchFlashAlerts(); // Refresh list after deletion
            } else {
                console.error("Failed to delete alert");
            }
        } catch (error) {
            console.error("Error deleting alert:", error);
        }
    };

    const updateAlertStatus = async (alertId: number, newStatus: "Active" | "Archived") => {
        try {
            const response = await fetch(`${BASE_URL}/flash-alerts/${alertId}/`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: newStatus }),
            });

            if (response.ok) {
                fetchFlashAlerts(); // Refresh list
            } else {
                console.error("Failed to deactivate alert");
            }
        } catch (error) {
            console.error("Error deactivating alert:", error);
        }
    };

    // The data officer approval module

    // Pagination
    const currentDrPage = mode === 'history' ? historyDrPage : recordsDrPage;
    const setCurrentDrPage = mode === 'history' ? setHistoryDrPage : setRecordsDrPage;
    const currentDrData = mode === 'history' ? history : records;
    const paginatedDrData = currentDrData.slice((currentDrPage - 1) * recordsDrPerPage, currentDrPage * recordsDrPerPage);
    const totalDrPages = Math.ceil(currentDrData.length / recordsDrPerPage);

    // Fetch available months
    const fetchMonths = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/raw-evictions/pending/months/`);
            const monthList = res.data.months || [];
            setMonths(monthList);

            if (monthList.length > 0) {
                setSelectedMonth('All Months'); // default to All
            }
        } catch (err) {
            console.error(err);
        }
    };

    // Fetch records based on selected month
    const fetchPending = async (month: string) => {
        setLoading(true);
        try {
            let url = `${BASE_URL}/raw-evictions/pending/`;
            if (month !== 'All Months') {
                url += `?month=${encodeURIComponent(month)}`;
            }

            const res = await axios.get(url);

            // Coalesce region / district here
            const mapped = res.data.map((r: any) => ({
                ...r,
                region: r.region ?? r.new_region ?? null,
                district: r.district ?? r.new_district ?? null,
            }));

            setRecords(mapped);
            setRecordsDrPage(1);
            // Pre-select all records in Single Mode
            const allIds = mapped.map((r: ProdEviction) => r._id);
            setSelectedIds(allIds);
        } catch (err) {
            console.error(err);
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingTotal = async () => {
        try {
            // Always hit the endpoint without any month filter
            const res = await axios.get(`${BASE_URL}/raw-evictions/pending/`);

            // You only need the length – no mapping required
            setTotalPendingCount(res.data.length);   // or whatever state you use
        } catch (err) {
            console.error(err);
            setTotalPendingCount(0);
        }
    };

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/raw-evictions/history/`);
            setHistory(res.data);
            setHistoryDrPage(1);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleModeChange = (newMode: Mode) => {
        setMode(newMode);
        if (newMode === 'history') {
            fetchHistory();
        } else {
            fetchPending(selectedMonth);
        }
    };


    useEffect(() => {
        fetchMonths();
    }, []);

    useEffect(() => {
        fetchPending(selectedMonth);
    }, [selectedMonth]);

    useEffect(() => {
        fetchHistory();
        fetchPendingTotal();
    }, []);

    const toggleSelection = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const approveRecords = async (ids: number[]) => {
        const operation = "approval";

        const userString = localStorage.getItem("e-user");
        let userEmail: string | null = null;
        if (userString && userString !== "undefined") {
            try {
                const user = JSON.parse(userString);
                userEmail = user?.user_email || null;
                console.log("🔍 Logged-in user email:", userEmail); // ✅ log it
            } catch (err) {
                console.error("Failed to parse user from localStorage", err);
            }
        }

        if (ids.length === 0) return;
        try {
            const res = await axios.patch(`${BASE_URL}/raw-evictions/approve/`,
                {
                    ids: selectedIds,
                    time_period: selectedMonth,
                    data_review_officer: userEmail,
                    operation: operation
                });
            alert(`✅ Successfully approved ${res.data.approved_count} records!`);
            fetchPending(selectedMonth);
            fetchHistory();
            fetchMonths();
            fetchPendingTotal();
        } catch (err: any) {
            alert(err.response?.data?.error || "Failed to approve records");
        }
    };

    const handleBulkApprove = () => approveRecords(records.map(r => r._id));
    const handleSingleApprove = () => approveRecords(selectedIds);

    const viewApprovedRecords = async (log: ApprovalLog) => {
        if (!log.record_ids || log.record_ids.length === 0) return;
        setSelectedHistoryIds(log.record_ids);
        setCurrentApprovalLog(log); // reuse the row you already have - it has data_operation
        setShowDetailModal(true);
        setLoading(true);

        try {
            const res = await axios.post(`${BASE_URL}/raw-evictions/details/`, { ids: log.record_ids });
            const mapped = res.data.map((r: any) => ({
                ...r,
                region: r.region ?? r.new_region ?? null,
                district: r.district ?? r.new_district ?? null,
            }));
            setModalRecords(mapped);
        } catch (err) {
            console.error(err);
            alert("Failed to load record details");
        } finally {
            setLoading(false);
        }
    };

    // --- Pagination slices ---
    const recordsTotalPages = Math.max(1, Math.ceil(records.length / recordsDrPerPage));
    const paginatedRecords = records.slice(
        (recordsDrPage - 1) * recordsDrPerPage,
        recordsDrPage * recordsDrPerPage
    );

    const historyTotalPages = Math.max(1, Math.ceil(history.length / recordsDrPerPage));
    const paginatedHistory = history.slice(
        (historyDrPage - 1) * recordsDrPerPage,
        historyDrPage * recordsDrPerPage
    );

    const MAX_VISIBLE_PAGES = 10;

    // Returns page numbers to render, collapsing the middle into '...' once
    // there are more than MAX_VISIBLE_PAGES pages. First and last page are
    // always shown so the user can always jump straight to either end.
    const getPageNumbers = (
        current: number,
        total: number,
        maxVisible: number = MAX_VISIBLE_PAGES
    ): (number | string)[] => {
        if (total <= maxVisible) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }

        const first = 1;
        const last = total;
        // Slots left for the window around `current`, after reserving
        // first, last, and up to two ellipses
        const windowSize = maxVisible - 4;
        const halfWindow = Math.floor(windowSize / 2);

        let windowStart = Math.max(first + 1, current - halfWindow);
        let windowEnd = Math.min(last - 1, current + halfWindow);

        if (windowStart <= first + 2) {
            windowStart = first + 1;
            windowEnd = windowStart + windowSize - 1;
        }
        if (windowEnd >= last - 2) {
            windowEnd = last - 1;
            windowStart = windowEnd - windowSize + 1;
        }
        windowStart = Math.max(windowStart, first + 1);
        windowEnd = Math.min(windowEnd, last - 1);

        const pages: (number | string)[] = [first];
        if (windowStart > first + 1) pages.push('...');
        for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
        if (windowEnd < last - 1) pages.push('...');
        pages.push(last);

        return pages;
    };

    const renderPaginationControls = (
        page: number,
        totalPages: number,
        onChange: (p: number) => void
    ) => {
        if (totalPages <= 1) return null;
        return (
            <div className="flex justify-center items-center gap-1.5 mt-6">
                <button
                    onClick={() => onChange(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm font-medium text-[#57534E] border border-[#E7E5E4] rounded-md
                           hover:border-[#D6D3D1] hover:bg-[#FAFAF9] transition-colors
                           disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                >
                    Previous
                </button>
                {getPageNumbers(page, totalPages).map((p, idx) =>
                    p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-sm text-[#A8A29E]">
                            …
                        </span>
                    ) : (
                        <button
                            key={p}
                            onClick={() => onChange(p as number)}
                            className={`min-w-[32px] px-2.5 py-1.5 text-sm font-mono rounded-md border transition-colors ${page === p
                                ? 'bg-[#C2410C] border-[#C2410C] text-white font-medium'
                                : 'border-[#E7E5E4] text-[#57534E] hover:border-[#D6D3D1] hover:bg-[#FAFAF9]'
                                }`}
                        >
                            {p}
                        </button>
                    )
                )}
                <button
                    onClick={() => onChange(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm font-medium text-[#57534E] border border-[#E7E5E4] rounded-md
                           hover:border-[#D6D3D1] hover:bg-[#FAFAF9] transition-colors
                           disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                >
                    Next
                </button>
            </div>
        );
    };
    const generatePDF = () => {
        console.log('currentApprovalLog at PDF time:', currentApprovalLog?.data_operation);
        const now = new Date();
        const timestamp = new Intl.DateTimeFormat("en-KE", {
            timeZone: "Africa/Nairobi",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
        })
            .format(now)
            .replace(/[/:, ]/g, "-");

        if (modalRecords.length === 0) return;

        const { jsPDF } = require('jspdf');
        const autoTable = require('jspdf-autotable').default;

        const doc = new jsPDF('portrait', 'mm', 'a4');
        const approverName = currentApprovalLog?.data_review_officer || "Data Review Officer";
        const recordOperation = currentApprovalLog?.data_operation || '—';
        // Header
        doc.setFontSize(16);
        doc.text("Eviction Records Approval Report", 14, 20);

        doc.setFontSize(11);
        doc.text(`Period: ${selectedMonth}`, 14, 28);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 35);
        doc.text(`Total Records: ${modalRecords.length}`, 14, 42);
        doc.text(`Record Operation: ${recordOperation}`, 14, 48);

        doc.line(14, 50, 196, 50);

        // Table with optimized column widths
        autoTable(doc, {
            head: [["ID", "Report Date", "Region", "District", "Households", "Individuals", "Eviction Reason"]],
            body: modalRecords.map(record => [
                record._id.toString(),
                record.report_date || '',
                record.region || '—',
                record.district || '—',
                record.evicted_households?.toString() || '0',
                record.evicted_individuals?.toString() || '0',
                (record.eviction_reasons || '').substring(0, 60) + (record.eviction_reasons?.length > 60 ? '...' : '')
            ]),
            startY: 55,
            styles: {
                fontSize: 9,
                cellPadding: 3,
                overflow: 'linebreak',
                halign: 'left'
            },
            headStyles: {
                fillColor: [37, 99, 235],
                textColor: 255,
                fontStyle: 'bold',
                halign: 'left'
            },
            columnStyles: {
                0: { cellWidth: 22 },   // ID
                1: { cellWidth: 25 },   // Date
                2: { cellWidth: 28 },   // Region
                3: { cellWidth: 30 },   // District
                4: { cellWidth: 20 },   // Households
                5: { cellWidth: 20 },   // Individuals
                6: { cellWidth: 45 }    // Reason (most flexible)
            },
            alternateRowStyles: { fillColor: [245, 247, 250] },
            margin: { top: 20, right: 14, bottom: 25, left: 14 },
        });
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(10);

            const approverText = `Approved by: ${approverName}`;
            const pageText = `Page ${i} of ${pageCount}`;

            // Left: Approved by
            doc.text(approverText, 14, doc.internal.pageSize.height - 10);

            // Right: Page Number
            const pageWidth = doc.internal.pageSize.width;
            doc.text(pageText, pageWidth - 14 - doc.getTextWidth(pageText), doc.internal.pageSize.height - 10);
        }

        doc.save(`approved_records_${selectedMonth.replace(/\s+/g, '_')}_${timestamp}.pdf`);
    };

    // Trimmed sample matching the real API shape, standing in for a fetch() call.
    useEffect(() => {
        const fetchEvictions = async () => {
            try {
                setLoading(true);
                setError(null);

                const res = await fetch(`${BASE_URL}/prod_eviction_stats/`);

                if (!res.ok) {
                    throw new Error(`HTTP ${res.status}`);
                }

                const data = await res.json();

                // Extract the specific key
                setEvictionsByYearPerMonth(data.evictionsByYearPerMonth);
            } catch (err) {
                console.error(err);
                setError(err instanceof Error ? err.message : 'Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        fetchEvictions();
    }, []);


    const toBase64 = async (url: string) => {
        const res = await fetch(url);
        const blob = await res.blob();
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };
    const handleGenerate = async (filters: ReportFilters) => {
        if (!evictionsByYearPerMonth) return; // data hasn't loaded yet

        const iconsBase64 = {
            evictionHouseholds: await toBase64(evictionHouseholds),
            evictionIndividuals: await toBase64(evictionIndividuals),
            preventionHouseholds: await toBase64(preventionHouseholds),
            preventionIndividuals: await toBase64(preventionIndividuals),
            riskDistricts: await toBase64(riskDistricts),
            riskTotalHouseholds: await toBase64(riskTotalHouseholds),
            riskExtreme: await toBase64(riskExtreme),
        };
        await downloadEvictionReport(
            evictionsByYearPerMonth,
            filters,
            riskByYearMonthDistrict ?? undefined,
            infrastructureByYearPerMonth ?? undefined,
            preventionsByYearPerMonth ?? undefined,
            { geojson: geojson ?? undefined, iconsBase64 },
        );
    };

    return (
        <div className="w-full flex justify-center p-2">
            <div className="w-full max-w-screen-lg">
                {/* Tab Buttons */}
                <div className="relative z-10 bg-white rounded-lg shadow-md overflow-hidden">
                    {/* Desktop */}
                    <div className="hidden sm:grid grid-cols-5 gap-0 border-b border-[#E7E5E4]">
                        {tabItems.map(({ id, label, icon: Icon, count }) => (
                            <button
                                key={id}
                                onClick={() => setTab(id)}
                                type="button"
                                className={`
                flex flex-col items-center justify-center gap-1.5 px-3 py-3.5
                border-b-2 text-center transition-colors duration-200
                ${tab === id
                                        ? "text-[#C2410C] border-[#C2410C] bg-white"
                                        : "text-[#78716C] border-transparent hover:text-[#9A3412] hover:bg-[#FFF7ED]"
                                    }
            `}
                            >
                                <span className="flex items-center gap-1">
                                    <Icon size={20} strokeWidth={tab === id ? 2.25 : 1.75} />
                                    {typeof count === "number" && count > 0 && (
                                        <span
                                            className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1
                                   rounded-full bg-[#C2410C] text-white text-[10px] font-mono font-semibold leading-none"
                                        >
                                            {count > 99 ? "99+" : count}
                                        </span>
                                    )}
                                </span>
                                <span className="text-xs font-medium tracking-tight whitespace-nowrap">
                                    {label}
                                </span>
                            </button>
                        ))}
                    </div>


                    {/* Mobile fallback (scrollable) — kept horizontal to save width */}
                    <div className="sm:hidden overflow-x-auto hide-scrollbar">
                        <div className="flex w-max min-w-full">
                            {tabItems.map(({ id, label, icon: Icon, count }) => (
                                <button
                                    key={id}
                                    onClick={() => setTab(id)}
                                    type="button"
                                    className={`
                    flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap
                    border-b-2 mx-1 first:ml-2 last:mr-2 transition-colors duration-200
                    ${tab === id
                                            ? "text-[#C2410C] border-[#C2410C]"
                                            : "text-[#78716C] border-transparent hover:text-[#9A3412]"
                                        }
                `}
                                >
                                    <Icon size={16} strokeWidth={2} />
                                    {label}

                                    {typeof count === "number" && (
                                        <span
                                            className={`
                            inline-flex items-center justify-center min-w-[18px] h-[18px] px-1
                            rounded-full text-[11px] font-mono font-semibold leading-none
                            ${count > 0
                                                    ? "bg-[#C2410C] text-white"
                                                    : "bg-[#F0EEEC] text-[#A8A29E]"
                                                }
                        `}
                                        >
                                            {count}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="mt-6 p-6 rounded-md bg-white shadow-sm min-h-[200px]">
                    {tab === 2 &&
                        <div>
                            <div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                                    {[
                                        {
                                            title: 'Evictions',
                                            value: evictionData?.individuals.toLocaleString() ?? '0',
                                            change: '+12%',
                                            icon: <Home className="w-5 h-5" style={{ color: '#dc2626' }} />,
                                            bg: '#fee2e2'
                                        },
                                        {
                                            title: 'Preventions',
                                            value: evictionData?.protected.toLocaleString() ?? '0',
                                            change: '-8%',
                                            icon: <Shield className="w-5 h-5" style={{ color: '#16a34a' }} />,
                                            bg: '#d1fae5'
                                        },
                                        {
                                            title: 'Households',
                                            value: evictionData?.households.toLocaleString() ?? '0',
                                            change: '+5%',
                                            icon: <Users className="w-5 h-5" style={{ color: '#2563eb' }} />,
                                            bg: '#dbeafe'
                                        },
                                        {
                                            title: 'Individuals at Risk',
                                            value: '4,521',
                                            change: '+3%',
                                            icon: <AlertTriangle className="w-5 h-5" style={{ color: '#ca8a04' }} />,
                                            bg: '#fef9c3'
                                        }
                                    ].map((stat) => (
                                        <div
                                            key={stat.title}
                                            style={{
                                                backgroundColor: '#ffffff',
                                                padding: '1.5rem',
                                                borderRadius: '0.5rem',
                                                border: '1px solid #e5e7eb', // Tailwind border-gray-200
                                                boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' // Tailwind shadow-sm
                                            }}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div
                                                    className="rounded-full p-3"
                                                    style={{ backgroundColor: stat.bg }}
                                                >
                                                    {stat.icon}
                                                </div>
                                                <div className="text-right">
                                                    <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>{stat.value}</p> {/* text-gray-900 */}
                                                    <p style={{ fontSize: '0.875rem', color: '#4b5563' }}>{stat.title}</p> {/* text-gray-600 */}
                                                    <span
                                                        style={{
                                                            fontSize: '0.75rem',
                                                            color: stat.change.includes('+') ? '#16a34a' : '#dc2626'
                                                        }}
                                                    >
                                                        {stat.change} vs last month
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mb-6">
                                    {/* Header */}
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-semibold" style={{ color: '#1f2937' }}>
                                            Data Insights
                                        </h3>
                                        <button
                                            className="px-4 py-2 rounded-lg text-sm flex items-center cursor-pointer whitespace-nowrap hover:bg-[#4338ca]"
                                            onClick={() => setShowBackendModal(true)}
                                            style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
                                        >
                                            <Upload className="w-4 h-4 mr-2" />
                                            Upload Data
                                        </button>
                                    </div>

                                    {/* Responsive Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Last Upload Dates */}
                                        <div className="bg-white p-6 rounded-lg border" style={{ borderColor: '#e5e7eb' }}>
                                            <h4 className="text-sm font-semibold mb-4" style={{ color: '#374151' }}>
                                                Last Upload Dates
                                            </h4>
                                            <div className="space-y-4">
                                                {Object.entries(staticDisplayConfig).map(([key, config], index) => {
                                                    const upload = latestUploads[key];
                                                    const date = upload?.timestamp ? new Date(upload.timestamp).toLocaleDateString() : 'No uploads yet';

                                                    return (
                                                        <div
                                                            key={index}
                                                            className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50"
                                                            style={{ borderColor: '#f3f4f6' }}
                                                        >
                                                            <div className="flex items-center space-x-3">
                                                                <div
                                                                    className="w-10 h-10 rounded-full flex items-center justify-center"
                                                                    style={{ backgroundColor: config.color.bg, color: config.color.text }}
                                                                >
                                                                    {config.icon}
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-medium" style={{ color: '#1f2937' }}>
                                                                        {config.type}
                                                                    </p>
                                                                    <p className="text-xs" style={{ color: '#6b7280' }}>
                                                                        Last updated: {date}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                className="text-sm flex items-center cursor-pointer whitespace-nowrap hover:text-[#3730a3]"
                                                                style={{ color: '#4f46e5' }}
                                                            >
                                                                <Upload className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                        </div>

                                        {/* Recent Upload Status */}
                                        <div className="bg-white p-6 rounded-lg border" style={{ borderColor: '#e5e7eb' }}>
                                            <h4 className="text-sm font-semibold mb-4" style={{ color: '#374151' }}>
                                                Recent Upload Status
                                            </h4>
                                            <div className="space-y-4">
                                                {uploadLogs.slice(0, 3).map((record, index) => {
                                                    const isSuccess = record.status === 'success';
                                                    const date = `${new Date(record.timestamp).toISOString().slice(0, 10)} : ${new Date(record.timestamp).toTimeString().slice(0, 5)}`;

                                                    return (
                                                        <div
                                                            key={index}
                                                            className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50"
                                                            style={{ borderColor: '#f3f4f6' }}
                                                        >
                                                            <div>
                                                                <p className="text-sm font-medium" style={{ color: '#1f2937' }}>
                                                                    {record.file_name}
                                                                </p>
                                                                <div className="flex items-center space-x-2 mt-1">
                                                                    <span
                                                                        className="px-2 py-0.5 text-xs rounded-full"
                                                                        style={{
                                                                            backgroundColor: isSuccess ? '#dcfce7' : '#fee2e2',
                                                                            color: isSuccess ? '#166534' : '#991b1b',
                                                                        }}
                                                                    >
                                                                        {record.status}
                                                                    </span>
                                                                    <span className="text-xs" style={{ color: '#6b7280' }}>
                                                                        {record.record_count} rows
                                                                    </span>
                                                                    <span className="text-xs" style={{ color: '#6b7280' }}>
                                                                        {date}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <button className="text-gray-400 hover:text-gray-600 cursor-pointer">
                                                                <ChevronRight className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                        </div>
                                    </div>
                                </div>

                                {/* Upload History */}
                                <div className="mt-8">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-medium" style={{ color: "#111827" }}>Upload History</h3>
                                    </div>
                                    <div className="overflow-x-auto border border-[#e5e7eb] rounded-lg">
                                        <table className="min-w-full divide-y" style={{ borderColor: "#E5E7EB" }}>
                                            <thead style={{ backgroundColor: "#F9FAFB" }}>
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: "#6B7280" }}>Date</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: "#6B7280" }}>Filename</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: "#6B7280" }}>Added By</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: "#6B7280" }}>Status</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: "#6B7280" }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody style={{ backgroundColor: "#FFFFFF", borderColor: "#E5E7EB" }}>
                                                {uploadLogs.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: "#6B7280" }}>
                                                            {new Date(item.timestamp).toLocaleDateString()} {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: "#6B7280" }}>
                                                            {item.file_name}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: "#6B7280" }}>
                                                            {item.user_email}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span
                                                                className="px-2 py-1 text-xs rounded-full"
                                                                style={{
                                                                    backgroundColor: item.status === 'success' ? "#D1FAE5" : "#FEE2E2",
                                                                    color: item.status === 'success' ? "#065F46" : "#991B1B"
                                                                }}
                                                            >
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm flex space-x-3">
                                                            <button
                                                                style={{ color: "#4F46E5" }}
                                                                onMouseOver={(e) => e.currentTarget.style.color = "#312E81"}
                                                                onMouseOut={(e) => e.currentTarget.style.color = "#4F46E5"}
                                                            >
                                                                <Eye size={16} />
                                                            </button>
                                                            <button
                                                                style={{ color: "#DC2626" }}
                                                                onMouseOver={(e) => e.currentTarget.style.color = "#7F1D1D"}
                                                                onMouseOut={(e) => e.currentTarget.style.color = "#DC2626"}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>


                                    {/* Pagination */}
                                    <div className="mt-4 flex justify-between items-center">
                                        <div className="text-sm" style={{ color: "#6B7280" }}>
                                            Showing <span className="font-medium">1</span> to <span className="font-medium">{uploadHistory.length}</span> of <span className="font-medium">{uploadHistory.length}</span> results
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                className="px-3 py-1 border rounded text-sm"
                                                style={{ color: "#4B5563", borderColor: "#D1D5DB" }}
                                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#F9FAFB"}
                                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                            >
                                                Previous
                                            </button>
                                            <button
                                                className="px-3 py-1 border rounded text-sm"
                                                style={{
                                                    backgroundColor: "#EEF2FF",
                                                    color: "#4F46E5",
                                                    borderColor: "#C7D2FE"
                                                }}
                                            >
                                                1
                                            </button>
                                            <button
                                                className="px-3 py-1 border rounded text-sm"
                                                style={{ color: "#4B5563", borderColor: "#D1D5DB" }}
                                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#F9FAFB"}
                                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    {tab === 1 &&
                        <div>
                            <div style={{ backgroundColor: '#ffffff' }} className="rounded-lg shadow-sm p-6">
                                {/* Section Header */}
                                <div className="mb-6">
                                    <h2 style={{ color: '#1f2937' }} className="text-2xl font-semibold text-gray-900 dark:text-white">
                                        Report Downloads
                                    </h2>
                                    <p style={{ color: '#4b5563' }} className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                        Generate and download your reports. Currently supports PDF, with more formats coming soon.
                                    </p>
                                </div>

                                {/* 3 Download Cards */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">

                                    {/* 1. PDF */}
                                    <div className="rounded-xl border border-[#E5E7EB] dark:border-[#374151] p-5 flex flex-col">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10 shrink-0">
                                                <FileText className="h-5 w-5 text-[#F97316]" />
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="font-medium text-[#111827] dark:text-white">
                                                    PDF Report
                                                </h3>
                                                {/* Button replaces "Available now" */}
                                                <div className="mt-0.5">
                                                    <GenerateReportButton onGenerate={handleGenerate} />
                                                </div>
                                            </div>
                                        </div>

                                        <p className="text-sm text-[#6B7280] dark:text-[#9CA3AF]">
                                            Full formatted report ready for sharing or printing.
                                        </p>
                                    </div>

                                    {/* 2. CSV */}
                                    <div className="rounded-xl border border-[#E5E7EB] dark:border-[#374151] p-5 flex flex-col">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10 shrink-0">
                                                <Database className="h-5 w-5 text-[#F97316]" />
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="font-medium text-[#111827] dark:text-white">
                                                    Eviction-Prevention Dataset
                                                </h3>
                                                <p className="text-xs text-[#6B7280] dark:text-[#9CA3AF] font-medium mt-0.5">
                                                    (Coming soon)
                                                </p>
                                            </div>
                                        </div>

                                        <p className="text-sm text-[#6B7280] dark:text-[#9CA3AF]">
                                            Raw dataset for eviction-prevention analysis and research.
                                        </p>
                                    </div>

                                    {/* 3. Suggested third option */}
                                    <div className="rounded-xl border border-[#E5E7EB] dark:border-[#374151] p-5 flex flex-col">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10 shrink-0">
                                                <Map className="h-5 w-5 text-[#F97316]" />
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="font-medium text-[#111827] dark:text-white">
                                                    RiskMap Dataset
                                                </h3>
                                                <p className="text-xs text-[#6B7280] dark:text-[#9CA3AF] font-medium mt-0.5">
                                                    (Coming soon)
                                                </p>
                                            </div>
                                        </div>

                                        <p className="text-sm text-[#6B7280] dark:text-[#9CA3AF]">
                                            Interactive risk visualization and geographic insights.
                                        </p>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <h2 style={{ color: '#1f2937' }} className="text-2xl font-bold">Document Repository</h2>
                                    <p style={{ color: '#4b5563' }} className="mt-1">Manage and organize eviction-related documentation and resources.</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                    {['reports', 'dashboard', 'evictionresources'].map((category) => (
                                        <div
                                            key={category}
                                            style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}
                                            className="rounded-lg p-6 border"
                                        >
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 style={{ color: '#1f2937' }} className="text-lg font-semibold">
                                                    {category}
                                                </h3>
                                                <button style={{ color: '#4f46e5' }} className="hover:text-[#3730a3] cursor-pointer whitespace-nowrap">
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <p style={{ color: '#6b7280' }} className="text-sm mb-2">
                                                Document count: {groupedFiles[category]?.length || 0}
                                            </p>
                                            <button
                                                style={{ color: '#4f46e5' }}
                                                className="text-sm hover:text-[#3730a3] flex items-center cursor-pointer whitespace-nowrap"
                                            >
                                                <span>View all</span>
                                                <ChevronRight className="ml-1 text-xs" />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                                    <h3 style={{ color: '#1f2937' }} className="text-lg font-semibold">
                                        Recent Documents
                                    </h3>
                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
                                        <div className="relative w-full sm:w-auto">
                                            <Search
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                                style={{ color: '#d1d5db' }}
                                                size={16}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Search documents..."
                                                style={{
                                                    paddingLeft: '2.5rem',
                                                    paddingRight: '1rem',
                                                    paddingTop: '0.5rem',
                                                    paddingBottom: '0.5rem',
                                                    borderWidth: '1px',
                                                    borderStyle: 'solid',
                                                    borderColor: '#d1d5db',
                                                    backgroundColor: '#f9fafb',
                                                    borderRadius: '0.5rem',
                                                    fontSize: '0.875rem'
                                                }}
                                                className="w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                                            />
                                        </div>
                                        <button
                                            style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
                                            className="px-4 py-2 rounded-lg hover:bg-[#4338ca] text-sm flex items-center cursor-pointer whitespace-nowrap"
                                            onClick={() => setShowAddModal(true)}
                                        >
                                            <Upload className="w-4 h-4 mr-2" />
                                            Upload New
                                        </button>
                                    </div>
                                </div>


                                <div className="overflow-x-auto border border-[#e5e7eb] rounded-lg">
                                    <table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
                                        <thead style={{ backgroundColor: '#f9fafb' }}>
                                            <tr>
                                                {['Name', 'Category', 'Added', 'Actions'].map((header) => (
                                                    <th
                                                        key={header}
                                                        style={{
                                                            padding: '0.75rem 1.5rem',
                                                            textAlign: 'left',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '500',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                            color: '#6b7280',
                                                            borderBottom: '1px solid #e5e7eb'
                                                        }}
                                                    >
                                                        {header}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedReports.map((doc, index) => (
                                                <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                    <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <div
                                                                style={{
                                                                    width: '2rem',
                                                                    height: '2rem',
                                                                    borderRadius: '0.375rem',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    marginRight: '0.75rem',
                                                                    backgroundColor: '#fee2e2',
                                                                    color: '#b91c1c'
                                                                }}
                                                            >
                                                                <i className="fas fa-file-pdf"></i>
                                                            </div>
                                                            <div>
                                                                <div
                                                                    style={{
                                                                        fontSize: '0.875rem',
                                                                        fontWeight: 500,
                                                                        color: '#111827',
                                                                        maxWidth: '300px',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                    title={doc.name}
                                                                >
                                                                    {doc.name}
                                                                </div>

                                                                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                                                    .pdf
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>
                                                        <span
                                                            style={{
                                                                padding: '0.25rem 0.5rem',
                                                                fontSize: '0.75rem',
                                                                borderRadius: '9999px',
                                                                backgroundColor: '#f3f4f6',
                                                                color: '#1f2937'
                                                            }}
                                                        >
                                                            {doc.category || 'Uncategorized'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#6b7280' }}>
                                                        {new Date(doc.uploaded_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex justify-center items-center space-x-3">
                                                            <a
                                                                href={doc.file_url}
                                                                download
                                                                className="text-[#4f46e5] hover:text-[#312e81]"
                                                            >
                                                                <Download className="w-4 h-4" />
                                                            </a>

                                                            <button
                                                                className="text-[#4b5563] hover:text-[#111827]"
                                                                onClick={() => {
                                                                    const newTitle = prompt("Enter new title", doc.name);
                                                                    if (newTitle && newTitle.trim() !== "") {
                                                                        // handle rename
                                                                    }
                                                                }}
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </button>

                                                            <button
                                                                className="text-[#dc2626] hover:text-[#7f1d1d]"
                                                                onClick={() => handleDeleteReport(doc.id)}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </td>

                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                </div>



                                <div className="mt-4 flex justify-between items-center">
                                    <div style={{ color: '#6b7280' }} className="text-sm">
                                        Showing{" "}
                                        <span style={{ fontWeight: 500 }}>
                                            {(reportPage - 1) * reportsPerPage + 1}
                                        </span>{" "}
                                        to{" "}
                                        <span style={{ fontWeight: 500 }}>
                                            {Math.min(reportPage * reportsPerPage, totalReportItems)}
                                        </span>{" "}
                                        of <span style={{ fontWeight: 500 }}>{totalReportItems}</span> documents
                                    </div>

                                    <div className="flex space-x-2">
                                        <button
                                            disabled={reportPage === 1}
                                            onClick={() => setReportPage((p) => p - 1)}
                                            className="px-3 py-1 border rounded text-sm"
                                            style={{
                                                color: '#4b5563',
                                                borderColor: '#d1d5db',
                                                opacity: reportPage === 1 ? 0.5 : 1,
                                            }}
                                        >
                                            Previous
                                        </button>

                                        {Array.from({ length: totalReportPages }, (_, i) => i + 1).map((page) => (
                                            <button
                                                key={page}
                                                onClick={() => setReportPage(page)}
                                                className="px-3 py-1 border rounded text-sm"
                                                style={{
                                                    backgroundColor: reportPage === page ? '#eef2ff' : 'transparent',
                                                    color: reportPage === page ? '#4f46e5' : '#4b5563',
                                                    borderColor: reportPage === page ? '#c7d2fe' : '#d1d5db',
                                                }}
                                            >
                                                {page}
                                            </button>
                                        ))}

                                        <button
                                            disabled={reportPage === totalReportPages}
                                            onClick={() => setReportPage((p) => p + 1)}
                                            className="px-3 py-1 border rounded text-sm"
                                            style={{
                                                color: '#4b5563',
                                                borderColor: '#d1d5db',
                                                opacity: reportPage === totalReportPages ? 0.5 : 1,
                                            }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {/* Donor Logos Icon Grid */}
                            <div style={{ backgroundColor: '#ffffff' }} className="rounded-lg shadow-sm p-6">
                                <div className="mb-6">
                                    <h2 className="text-2xl font-bold text-[#1f2937]">Upload & Manage Logos</h2>
                                    <p className="mt-1 text-[#4b5563] text-sm">Add, update, or remove organization logos by category.</p>
                                </div>

                                <div className="mb-12 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                                    <h3 style={{ color: '#1f2937' }} className="text-lg font-semibold">
                                        Our Partners
                                    </h3>
                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
                                        <div className="relative w-full sm:w-auto">
                                            <Search
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                                style={{ color: '#d1d5db' }}
                                                size={16}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Search partners..."
                                                style={{
                                                    paddingLeft: '2.5rem',
                                                    paddingRight: '1rem',
                                                    paddingTop: '0.5rem',
                                                    paddingBottom: '0.5rem',
                                                    borderWidth: '1px',
                                                    borderStyle: 'solid',
                                                    borderColor: '#d1d5db',
                                                    backgroundColor: '#f9fafb',
                                                    borderRadius: '0.5rem',
                                                    fontSize: '0.875rem'
                                                }}
                                                className="w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                                            />
                                        </div>
                                        <button
                                            style={{ backgroundColor: '#4f46e5', color: '#ffffff' }}
                                            className="px-4 py-2 rounded-lg hover:bg-[#4338ca] text-sm flex items-center cursor-pointer whitespace-nowrap"
                                            onClick={() => setShowPartnersModal(true)}
                                        >
                                            <Upload className="w-4 h-4 mr-2" />
                                            Add Partner
                                        </button>
                                    </div>
                                </div>

                                <div className="flex gap-2 mb-6">
                                    <button
                                        className={`px-4 py-2 rounded-md text-sm font-medium ${activeTab === "donor_partners" ? "bg-[#4F46E5] text-white" : "bg-[#F3F4F6] text-[#111827]"
                                            }`}
                                        onClick={() => setActiveTab("donor_partners")}
                                    >
                                        Donor Partners
                                    </button>
                                    <button
                                        className={`px-4 py-2 rounded-md text-sm font-medium ${activeTab === "data_partners" ? "bg-[#4F46E5] text-white" : "bg-[#F3F4F6] text-[#111827]"
                                            }`}
                                        onClick={() => setActiveTab("data_partners")}
                                    >
                                        Data Partners
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {paginatedPartners.map((doc: Partner) => (
                                        <div
                                            key={doc.id}
                                            className="group relative bg-[#FFFFFF] rounded-lg border border-[#E5E7EB] overflow-hidden hover:shadow-lg transition-all"
                                        >
                                            <div className="aspect-w-3 aspect-h-4 bg-[#F3F4F6]">
                                                <img
                                                    src={doc.thumbnail_url}
                                                    alt={doc.title}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center gap-2">
                                                    <a
                                                        href={doc.file_url}
                                                        download
                                                        className="opacity-0 group-hover:opacity-100 px-4 py-2 bg-white text-[#111827] rounded-lg shadow-lg text-sm font-medium transform translate-y-4 group-hover:translate-y-0 transition-all"
                                                    >
                                                        <Download size={16} className="mr-1 inline" />
                                                        Download
                                                    </a>
                                                    <button
                                                        onClick={() => handleDeletePartner(doc.id)}
                                                        className="opacity-0 group-hover:opacity-100 px-3 py-2 bg-[#DC2626] text-white rounded-lg shadow-lg text-sm font-medium transform translate-y-4 group-hover:translate-y-0 transition-all"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>

                                            </div>
                                            <div className="p-4">
                                                <h3 className="text-sm font-medium text-[#111827] truncate" title={doc.title}>
                                                    {doc.title}
                                                </h3>
                                                <div className="mt-2 flex items-center justify-between text-xs text-[#6B7280]">
                                                    <div className="flex items-center gap-1">
                                                        <FileTextIcon size={14} className="text-[#4F46E5]" />
                                                        <span>IMG</span>
                                                    </div>
                                                    <span>{new Date(doc.created_at).toISOString().split("T")[0]}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {totalPages > 1 && (
                                    <div className="flex justify-center mt-6 gap-2">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`px-3 py-1 rounded-md text-sm ${page === currentPage
                                                    ? "bg-[#4F46E5] text-white"
                                                    : "bg-[#F3F4F6] text-[#374151]"
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                    </div>

                                )}
                            </div>
                        </div>
                    }
                    {tab === 3 &&
                        <div>
                            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                                <div className="mb-6">
                                    <h2 className="text-2xl font-bold text-gray-800">Flash Alerts Management</h2>
                                    <p className="mt-1" style={{ color: '#4b5563' }}>Create and manage emergency notifications and alerts</p>
                                </div>

                                {/* Alert Statistics */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                                    {[
                                        { title: "All Alerts", count: flashAlerts.length, icon: <Clock size={20} />, bg: "#e0f2fe", color: "#0284c7", },
                                        { title: "Active Alerts", count: flashAlerts.filter((a) => a.status === "Active").length, icon: <Bell size={20} />, bg: "#fee2e2", color: "#dc2626", },
                                        { title: "Draft", count: flashAlerts.filter((a) => a.status === "Draft").length, icon: <FileText size={20} />, bg: "#f3f4f6", color: "#4b5563", },
                                        { title: "Archived", count: flashAlerts.filter((a) => a.status === "Archived").length, icon: <Archive size={20} />, bg: "#fef9c3", color: "#ca8a04", },
                                    ].map((stat) => (
                                        <div
                                            key={stat.title}
                                            className="bg-white p-6 rounded-lg border border-[#e5e7eb] border-gray-200 shadow-sm"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div
                                                    className="rounded-full p-3"
                                                    style={{ backgroundColor: stat.bg, color: stat.color }}
                                                >
                                                    {stat.icon}
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
                                                    <p className="text-sm" style={{ color: "#4b5563" }}>
                                                        {stat.title}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-8">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-lg font-semibold text-gray-800">Manage Alerts</h3>
                                    <div className="flex items-center space-x-3">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Search alerts..."
                                                className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1] border-[#D1D5DB]"
                                            />
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                                        </div>
                                        <button className="px-4 py-2 bg-[#4F46E5] text-white rounded-lg hover:bg-[#4338CA] text-sm flex items-center"
                                            onClick={() => setShowFlashAlertsModal(true)}>
                                            <Plus size={16} className="mr-2" />
                                            New Alert
                                        </button>
                                    </div>
                                </div>

                                {/* Filters */}
                                <div className="flex flex-wrap gap-3 mb-6">
                                    {["All Alerts", "Active", "Draft", "Archived"].map((label) => (
                                        <button
                                            key={label}
                                            onClick={() => {
                                                setFilter(label);    // 👈 set the filter state
                                                setFlashPage(1);     // 👈 reset pagination when filter changes
                                            }}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg ${filter === label
                                                ? "bg-[#EEF2FF] text-[#4F46E5] border border-[#C7D2FE]"
                                                : "bg-white border border-[#D1D5DB] text-[#374151] hover:bg-[#F9FAFB]"
                                                }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>


                                {/* Alert List */}
                                <div className="space-y-4">
                                    {currentAlerts.map((alert, index) => {
                                        const statusColor =
                                            alert.status === 'Active'
                                                ? { bg: '#DCFCE7', text: '#166534' }
                                                : alert.status === 'Scheduled'
                                                    ? { bg: '#DBEAFE', text: '#1D4ED8' }
                                                    : alert.status === 'Draft'
                                                        ? { bg: '#F3F4F6', text: '#374151' }
                                                        : { bg: '#FEF9C3', text: '#CA8A04' };

                                        return (
                                            <div
                                                key={index}
                                                className="bg-white p-5 rounded-lg border border-[#E5E7EB] shadow-sm hover:shadow-md transition-shadow"
                                            >
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="flex items-center space-x-3">

                                                        <div>
                                                            <h4 className="text-base font-medium text-gray-900">{alert.title}</h4>
                                                            <div className="flex items-center space-x-2 mt-1">
                                                                <span
                                                                    className="px-2 py-1 text-xs rounded-full"
                                                                    style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
                                                                >
                                                                    {alert.status}
                                                                </span>
                                                                <span className="text-xs text-gray-500">{alert.created_at}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex space-x-2">
                                                        <button className="p-2 text-[#9CA3AF] hover:text-[#4B5563]">
                                                            <Edit size={16} />
                                                        </button>
                                                        <button className="p-2 text-[#9CA3AF] hover:text-[#4B5563]">
                                                            <Copy size={16} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteAlert(alert.id)}
                                                            className="p-2 text-[#F87171] hover:text-[#DC2626]">
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="pl-13 ml-13">
                                                    <p className="text-sm text-[#4B5563] mb-3">
                                                        {!showFullDescription
                                                            ? `${alert.description.slice(0, 250)}... `
                                                            : `${alert.description} `}
                                                        {alert.description.length > 120 && (
                                                            <span
                                                                onClick={() => setShowFullDescription(!showFullDescription)}
                                                                className="text-[#2563EB] hover:text-[#1D4ED8] hover:underline cursor-pointer"
                                                            >
                                                                {showFullDescription ? "Show less" : "Show more"}
                                                            </span>
                                                        )}
                                                    </p>

                                                    <div className="flex flex-wrap gap-3 text-xs text-[#6B7280]">
                                                        <div className="flex items-center">
                                                            <MapPin size={12} className="mr-1" />
                                                            <span>{alert.location}</span>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <CalendarDays size={12} className="mr-1" />
                                                            <span>Added: {alert.date}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {alert.status === 'Active' && (
                                                    <div className="mt-4 flex justify-end">
                                                        <button
                                                            onClick={() => updateAlertStatus(alert.id, "Archived")}
                                                            className="px-3 py-1.5 bg-[#FEF2F2] text-[#DC2626] rounded border border-[#FECACA] text-xs font-medium hover:bg-[#FEE2E2]">
                                                            Deactivate Alert
                                                        </button>
                                                    </div>
                                                )}

                                                {alert.status === "Archived" && (
                                                    <div className="mt-4 flex justify-end">
                                                        <button
                                                            onClick={() => updateAlertStatus(alert.id, "Active")}
                                                            className="px-3 py-1.5 bg-[#ECFDF5] text-[#059669] rounded border border-[#D1FAE5] text-xs font-medium hover:bg-[#DDF6ED]"
                                                        >
                                                            Reactivate Alert
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Pagination */}
                                <div className="mt-6 flex justify-between items-center">
                                    <div className="text-sm text-gray-500">
                                        Showing{" "}
                                        <span className="font-medium">
                                            {(flashPage - 1) * alertsPerPage + 1}
                                        </span>{" "}
                                        to{" "}
                                        <span className="font-medium">
                                            {Math.min(flashPage * alertsPerPage, totalFlashItems)}
                                        </span>{" "}
                                        of <span className="font-medium">{totalFlashItems}</span> alerts
                                    </div>

                                    <div className="flex space-x-2">
                                        {/* Previous */}
                                        <button
                                            disabled={flashPage === 1}
                                            onClick={() => setFlashPage((p) => p - 1)}
                                            className="px-3 py-1 border rounded text-sm"
                                            style={{
                                                color: "#4b5563",
                                                borderColor: "#d1d5db",
                                                opacity: flashPage === 1 ? 0.5 : 1,
                                            }}
                                        >
                                            Previous
                                        </button>

                                        {/* Page numbers */}
                                        {Array.from({ length: totalFlashPages }, (_, i) => i + 1).map((page) => (
                                            <button
                                                key={page}
                                                onClick={() => setFlashPage(page)}
                                                className="px-3 py-1 border rounded text-sm"
                                                style={{
                                                    backgroundColor: flashPage === page ? "#eef2ff" : "transparent",
                                                    color: flashPage === page ? "#4f46e5" : "#4b5563",
                                                    borderColor: flashPage === page ? "#c7d2fe" : "#d1d5db",
                                                }}
                                            >
                                                {page}
                                            </button>
                                        ))}

                                        {/* Next */}
                                        <button
                                            disabled={flashPage === totalFlashPages || totalFlashItems === 0}
                                            onClick={() => setFlashPage((p) => p + 1)}
                                            className="px-3 py-1 border rounded text-sm"
                                            style={{
                                                color: "#4b5563",
                                                borderColor: "#d1d5db",
                                                opacity: flashPage === totalFlashPages || totalFlashItems === 0 ? 0.5 : 1,
                                            }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>

                            </div>

                        </div>
                    }
                    {tab === 4 &&
                        <div>
                            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                                <div className="mb-6">
                                    <h2 className="text-2xl font-bold text-gray-800">Needs Assessment Management</h2>
                                    <p className="mt-1" style={{ color: '#4b5563' }}>Track, update, and analyze humanitarian needs and response data efficiently.</p>
                                </div>

                                {/* Humanitarian Snapshot Overview */}
                                {/* Humanitarian Snapshot Overview */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                    {loading ? (
                                        Array(5).fill(null).map((_, idx) => (
                                            <div key={idx} className="bg-white p-6 rounded-lg border border-[#e5e7eb] shadow-sm animate-pulse space-y-3">
                                                <div className="rounded-full w-10 h-10 bg-gray-200" />
                                                <div className="h-6 bg-gray-200 rounded w-24" />
                                                <div className="h-4 bg-gray-100 rounded w-20" />
                                            </div>
                                        ))
                                    ) : snapshot ? (
                                        [
                                            {
                                                title: "Population in Need",
                                                value: snapshot.population_in_need.toLocaleString(),
                                                icon: <FaPeopleLine size={20} />,
                                                bg: "#fee2e2",
                                                color: "#dc2626",
                                            },
                                            {
                                                title: "Population Target",
                                                value: snapshot.population_target.toLocaleString(),
                                                icon: <FaArrowsDownToPeople size={20} />,
                                                bg: "#dbeafe",
                                                color: "#2563eb",
                                            },
                                            {
                                                title: "Population Reached",
                                                value: snapshot.population_reached.toLocaleString(),
                                                icon: <IoIosPeople size={20} />,
                                                bg: "#f3f4f6",
                                                color: "#4b5563",
                                            },
                                            {
                                                title: "Funding Required",
                                                value: `${formatFull(snapshot.funding_required.toLocaleString())}`,
                                                icon: <FaSackDollar size={20} />,
                                                bg: "#fef9c3",
                                                color: "#ca8a04",
                                            },
                                            {
                                                title: "Funding Received",
                                                value: `${formatFull(snapshot.funding_received.toLocaleString())}`,
                                                icon: <FaSackDollar size={20} />,
                                                bg: "#fef9c3",
                                                color: "#ca8a04",
                                            },
                                        ].map((stat) => (
                                            <div key={stat.title} className="bg-white p-4 rounded-lg border border-[#e5e7eb] shadow-sm">
                                                <div className="flex items-center justify-between">
                                                    <div
                                                        className="rounded-full p-2"
                                                        style={{ backgroundColor: stat.bg, color: stat.color }}
                                                    >
                                                        {stat.icon}
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                                                        <p className="text-xs" style={{ color: "#4b5563" }}>{stat.title}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-red-600 col-span-4">Failed to load snapshot.</p>
                                    )}
                                </div>


                            </div>

                            <div className="mb-8">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-lg font-semibold text-gray-800">Manage Alerts</h3>
                                    <div className="flex items-center space-x-3">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Search snapshot..."
                                                className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1] border-[#D1D5DB]"
                                            />
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                                        </div>
                                        <button className="px-4 py-2 bg-[#4F46E5] text-white rounded-lg hover:bg-[#4338CA] text-sm flex items-center"
                                            onClick={() => setShowSnapshotModal(true)}>
                                            <Plus size={16} className="mr-2" />
                                            Update Snapshot
                                        </button>
                                    </div>
                                </div>

                                {/* Filters */}
                                <div className="flex flex-wrap gap-3 mb-6">
                                    {['All Alerts', 'Active', 'Scheduled', 'Draft', 'Archived'].map((label) => (
                                        <button
                                            key={label}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg ${label === 'All Alerts'
                                                ? 'bg-[#EEF2FF] text-[#4F46E5]'
                                                : 'bg-white border border-[#D1D5DB] text-[#374151] hover:bg-[#F9FAFB]'
                                                }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>

                                {/* Snapshot Summary Card */}
                                {snapshot && (
                                    <div className="bg-white p-5 rounded-lg border border-[#E5E7EB] shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <h4 className="text-base font-medium text-gray-900">Latest Humanitarian Snapshot</h4>
                                                <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                                                    <CalendarDays size={12} className="mr-1" />
                                                    <span>
                                                        Reporting Period: {new Date(snapshot.report_year, snapshot.report_month - 1).toLocaleString("default", {
                                                            month: "long",
                                                            year: "numeric",
                                                        })}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="pl-13 ml-13">
                                            <p className="text-sm text-gray-600 mb-3">Snapshot Overview</p>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-700">
                                                <div className="flex items-center space-x-2">
                                                    <FaPeopleLine size={16} className="text-[#4A5568]" />
                                                    <span><strong>Population in Need:</strong> {Number(snapshot.population_in_need).toLocaleString()}</span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <FaArrowsDownToPeople size={16} className="text-[#4A5568]" />
                                                    <span><strong>Target:</strong> {Number(snapshot.population_target).toLocaleString()}</span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <IoIosPeople size={16} className="text-[#4A5568]" />
                                                    <span><strong>Reached:</strong> {Number(snapshot.population_reached).toLocaleString()}</span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <FaSackDollar size={16} className="text-[#4A5568]" />
                                                    <span><strong>Funding Required:</strong> ${Number(snapshot.funding_required).toLocaleString()}</span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <FaHandHoldingDollar size={16} className="text-[#4A5568]" />
                                                    <span><strong>Funding Received:</strong> ${Number(snapshot.funding_received).toLocaleString()}</span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <span><strong>Funding Coverage:</strong> {
                                                        snapshot.funding_required > 0
                                                            ? `${Math.round((snapshot.funding_received / snapshot.funding_required) * 100)}%`
                                                            : "N/A"
                                                    }</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {!loadingSnapshots && snapshots.length > 0 && (
                                    <div style={{ marginTop: '1.5rem' }}>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1F2937', marginBottom: '0.75rem' }}>
                                            Historical Snapshot Records
                                        </h3>
                                        <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '0.5rem' }}>
                                            <table style={{ minWidth: '100%', fontSize: '0.875rem', textAlign: 'left', color: '#374151' }}>
                                                <thead style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', textTransform: 'uppercase', fontWeight: '600', fontSize: '0.75rem', color: '#4B5563' }}>
                                                    <tr>
                                                        <th style={{ padding: '0.75rem 1rem' }}>Month</th>
                                                        <th style={{ padding: '0.75rem 1rem' }}>PiN</th>
                                                        <th style={{ padding: '0.75rem 1rem' }}>Target</th>
                                                        <th style={{ padding: '0.75rem 1rem' }}>Reached</th>
                                                        <th style={{ padding: '0.75rem 1rem' }}>Funding Required</th>
                                                        <th style={{ padding: '0.75rem 1rem' }}>Funding Received</th>
                                                        <th style={{ padding: '0.75rem 1rem' }}>Coverage</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedSnapshots.map((snap, idx) => (
                                                        <tr key={idx} style={{ borderBottom: '1px solid #E5E7EB', transition: 'background-color 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#F9FAFB'} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                            <td style={{ padding: '0.5rem 1rem' }}>
                                                                {new Date(snap.report_year, snap.report_month - 1).toLocaleString("default", {
                                                                    month: "short",
                                                                    year: "numeric",
                                                                })}
                                                            </td>
                                                            <td style={{ padding: '0.5rem 1rem' }}>{Number(snap.population_in_need).toLocaleString()}</td>
                                                            <td style={{ padding: '0.5rem 1rem' }}>{Number(snap.population_target).toLocaleString()}</td>
                                                            <td style={{ padding: '0.5rem 1rem' }}>{Number(snap.population_reached).toLocaleString()}</td>
                                                            <td style={{ padding: '0.5rem 1rem' }}>${Number(snap.funding_required).toLocaleString()}</td>
                                                            <td style={{ padding: '0.5rem 1rem' }}>${Number(snap.funding_received).toLocaleString()}</td>
                                                            <td style={{ padding: '0.5rem 1rem' }}>
                                                                {snap.funding_required > 0
                                                                    ? `${Math.round((snap.funding_received / snap.funding_required) * 100)}%`
                                                                    : "N/A"}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}


                                {/* Pagination */}
                                <div className="mt-6 flex justify-between items-center">
                                    <div className="text-sm" style={{ color: '#6B7280' }}>
                                        Showing{' '}
                                        <span style={{ fontWeight: 500 }}>
                                            {(snapshotCurrentPage - 1) * snapshotItemsPerPage + 1}
                                        </span>{' '}
                                        to{' '}
                                        <span style={{ fontWeight: 500 }}>
                                            {Math.min(snapshotCurrentPage * snapshotItemsPerPage, snapshotTotalItems)}
                                        </span>{' '}
                                        of <span style={{ fontWeight: 500 }}>{snapshotTotalItems}</span> snapshots
                                    </div>

                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => goToSnapshotPage(snapshotCurrentPage - 1)}
                                            disabled={snapshotCurrentPage === 1}
                                            className="px-3 py-1 border rounded text-sm"
                                            style={{
                                                color: '#4B5563',
                                                borderColor: '#D1D5DB',
                                                backgroundColor: snapshotCurrentPage === 1 ? '#F9FAFB' : 'transparent',
                                                cursor: snapshotCurrentPage === 1 ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            Previous
                                        </button>

                                        {Array.from({ length: snapshotTotalPages }, (_, i) => i + 1).map((page) => (
                                            <button
                                                key={page}
                                                onClick={() => goToSnapshotPage(page)}
                                                className="px-3 py-1 border rounded text-sm"
                                                style={{
                                                    backgroundColor: snapshotCurrentPage === page ? '#EEF2FF' : 'transparent',
                                                    color: snapshotCurrentPage === page ? '#4F46E5' : '#4B5563',
                                                    borderColor: snapshotCurrentPage === page ? '#C7D2FE' : '#D1D5DB',
                                                }}
                                            >
                                                {page}
                                            </button>
                                        ))}

                                        <button
                                            onClick={() => goToSnapshotPage(snapshotCurrentPage + 1)}
                                            disabled={snapshotCurrentPage === snapshotTotalPages}
                                            className="px-3 py-1 border rounded text-sm"
                                            style={{
                                                color: '#4B5563',
                                                borderColor: '#D1D5DB',
                                                backgroundColor: snapshotCurrentPage === snapshotTotalPages ? '#F9FAFB' : 'transparent',
                                                cursor: snapshotCurrentPage === snapshotTotalPages ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    }

                    {tab === 5 &&
                        <div className="p-8 bg-white rounded-xl border border-[#E7E5E4] shadow-sm">
                            {/* Accent rule */}
                            <div className="h-[2px] w-12 bg-[#C2410C] rounded-full mb-4" />

                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <p className="text-[11px] font-semibold tracking-[0.12em] text-[#C2410C] uppercase mb-1">
                                        Data Review
                                    </p>
                                    <h2 className="font-display text-xl font-semibold text-[#1C1917]">
                                        Review Pending Evictions
                                    </h2>
                                </div>
                            </div>

                            {/* Month Filter */}
                            <div className="flex items-center gap-3 mb-6">
                                <label className="text-sm font-medium text-[#57534E]">Month</label>
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    className="text-sm border border-[#E7E5E4] rounded-md px-3 py-1.5 text-[#1C1917] bg-white
                       focus:outline-none focus:ring-2 focus:ring-[#C2410C]/30 focus:border-[#C2410C]
                       hover:border-[#D6D3D1] transition-colors"
                                >
                                    <option value="All Months">All Months</option>
                                    {months.map(month => (
                                        <option key={month} value={month}>{month}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Mode Selector — segmented control with sliding underline */}
                            <div className="flex gap-6 mb-6 border-b border-[#E7E5E4]">
                                {modeTabs.map(tab => (
                                    <button
                                        key={tab.key}
                                        onClick={() => handleModeChange(tab.key)}
                                        className={`relative pb-3 text-sm font-medium transition-colors ${mode === tab.key
                                            ? 'text-[#1C1917]'
                                            : 'text-[#A8A29E] hover:text-[#57534E]'
                                            }`}
                                    >
                                        {tab.label}
                                        {mode === tab.key && (
                                            <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#C2410C] rounded-full" />
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Action Button */}
                            <div className="mb-6">
                                {mode === 'bulk' ? (
                                    <button
                                        onClick={handleBulkApprove}
                                        disabled={records.length === 0 || loading}
                                        className="bg-[#15803D] hover:bg-[#166534] text-white text-sm font-medium
                           px-5 py-2 rounded-md transition-colors disabled:bg-[#D6D3D1] disabled:cursor-not-allowed"
                                    >
                                        Approve all in {selectedMonth} ({records.length})
                                    </button>
                                ) : mode === 'single' ? (
                                    <button
                                        onClick={handleSingleApprove}
                                        disabled={selectedIds.length === 0 || loading}
                                        className="bg-[#15803D] hover:bg-[#166534] text-white text-sm font-medium
                           px-5 py-2 rounded-md transition-colors disabled:bg-[#D6D3D1] disabled:cursor-not-allowed"
                                    >
                                        Approve selected ({selectedIds.length})
                                    </button>
                                ) : null}
                            </div>

                            {/* Loading State */}
                            {loading && (
                                <p className="text-[#C2410C] text-sm font-medium mb-4">Loading records…</p>
                            )}

                            {/* HISTORY MODE */}
                            {mode === 'history' && (
                                <div>
                                    <table className="w-full border-collapse text-sm text-[#1C1917]">
                                        <thead>
                                            <tr className="border-b border-[#E7E5E4]">
                                                <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Date</th>
                                                <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Data Officer</th>
                                                <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Review Period</th>
                                                <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Approved</th>
                                                <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Record IDs</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-[#F0EEEC]">
                                            {paginatedHistory.map((log, index) => (
                                                <tr key={index} className="hover:bg-[#FAFAF9] transition-colors">
                                                    <td className="p-3 font-mono text-[13px] text-[#57534E]">{log.created_at}</td>
                                                    <td className="p-3">{log.data_review_officer}</td>
                                                    <td className="p-3">{log.time_period}</td>
                                                    <td className="p-3 font-mono text-[13px] font-medium">{log.record_count}</td>
                                                    <td className="p-3 text-[13px] text-[#78716C]">
                                                        <span
                                                            onClick={() => viewApprovedRecords(log)}
                                                            className="font-mono text-[#C2410C] hover:text-[#9A3412] underline decoration-[#C2410C]/30 hover:decoration-[#9A3412] cursor-pointer"
                                                        >
                                                            {log.record_ids?.slice(0, 8).join(", ") || '—'}
                                                            {log.record_ids?.length > 8 && " …"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {paginatedHistory.length === 0 && !loading && (
                                                <tr>
                                                    <td colSpan={5} className="p-8 text-center text-sm text-[#A8A29E]">
                                                        No approval history found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {renderPaginationControls(historyDrPage, historyTotalPages, setHistoryDrPage)}
                                </div>
                            )}

                            {/* Pending Records Table (Bulk & Single Mode) */}
                            {mode !== 'history' && (
                                <>
                                    {!loading && records.length === 0 && (
                                        <p className="text-sm text-[#A8A29E] text-center py-10">
                                            No pending records found for this month.
                                        </p>
                                    )}

                                    {records.length > 0 && (
                                        <table className="w-full border-collapse text-sm text-[#1C1917]">
                                            <thead>
                                                <tr className="border-b border-[#E7E5E4]">
                                                    <th className="p-3 w-12"></th>
                                                    <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">ID</th>
                                                    <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Date</th>
                                                    <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Region</th>
                                                    <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">District</th>
                                                    <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Households</th>
                                                    <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Individuals</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[#F0EEEC]">
                                                {paginatedRecords.map(record => (
                                                    <tr
                                                        key={record._id}
                                                        className={`transition-colors ${selectedIds.includes(record._id) ? 'bg-[#FFF7ED]' : 'hover:bg-[#FAFAF9]'
                                                            }`}
                                                    >
                                                        <td className="p-3 text-center">
                                                            {mode === 'single' && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedIds.includes(record._id)}
                                                                    onChange={() => toggleSelection(record._id)}
                                                                    className="accent-[#C2410C] w-4 h-4 cursor-pointer"
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="p-3 font-mono text-[13px] font-medium">{record._id}</td>
                                                        <td className="p-3 font-mono text-[13px] text-[#57534E]">{record.report_date || '—'}</td>
                                                        <td className="p-3">{record.region || '—'}</td>
                                                        <td className="p-3">{record.district || '—'}</td>
                                                        <td className="p-3 font-mono text-[13px]">{record.evicted_households || 0}</td>
                                                        <td className="p-3 font-mono text-[13px]">{record.evicted_individuals || 0}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    {renderPaginationControls(recordsDrPage, recordsTotalPages, setRecordsDrPage)}
                                </>
                            )}
                        </div>
                    }
                </div>
            </div>

            {showAddModal && <ModalLayout title="Add Download File">
                <div>
                    <form className="mt-5" onSubmit={handleUpload}>
                        <div className="mb-2 ">
                            <label
                                htmlFor="title"
                                className="mb-1 block text-black dark:text-white text-sm"
                            >
                                File Type
                            </label>
                            <select
                                id="type"
                                name="type"
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                className="border-gray w-full text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                            >
                                <option value=''>---Select---</option>
                                <option value='reports'>Reports</option>
                                <option value='dashboard'>Dashboards</option>
                                <option value='evictionresources'>Eviction Resources</option>
                            </select>
                        </div>
                        <div className="mb-2 ">
                            <label
                                htmlFor="file"
                                className="mb-1 block text-black dark:text-white text-sm"
                            >
                                File (pdf)
                            </label>
                            <input
                                type="file"
                                id="file"
                                name="file"
                                accept="application/pdf" // Only allow PDF files
                                onChange={(e) => setFile(e.target.files)}
                                className="border-gray w-full text-black rounded border-stroke bg-transparent py-1 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button type="button" className="py-2 px-4 rounded text-white bg-danger" onClick={() => setShowAddModal(false)}>Close</button>
                            <button
                                type="button"
                                className="py-2 px-4 rounded text-white bg-primary"
                                onClick={handleUpload}
                            >
                                Upload
                            </button>
                        </div>
                    </form>
                </div>
                {uploadStatus === "uploading" && (
                    <p className="text-sm text-blue-600 mb-2">Uploading file...</p>
                )}

                {uploadStatus === "success" && (
                    <p className="text-sm text-green-600 mb-2">Upload successful!</p>
                )}

                {uploadStatus === "error" && uploadError && (
                    <p className="text-sm text-red-600 mb-2">{uploadError}</p>
                )}
            </ModalLayout>}

            {showBackendModal && (
                <ModalLayout title="Upload File to Backend">
                    <form className="mt-5" onSubmit={(e) => {
                        e.preventDefault();
                        uploadFileToBackend(file?.[0] || null, type);
                    }}>
                        {/* Model Selector */}
                        <div className="mb-2">
                            <label htmlFor="model" className="mb-1 block text-black dark:text-white text-sm">
                                Select Model
                            </label>
                            <select
                                id="model"
                                name="model"
                                value={targetModel}
                                onChange={(e) => {
                                    console.log("Dropdown selected:", e.target.value);
                                    setTargetModel(e.target.value);
                                }}
                                className="border-gray w-full text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                            >
                                <option value="" disabled hidden>---Select Model---</option>
                                <option value="evictions">Evictions</option>
                                <option value="preventions">Preventions</option>
                                <option value="riskmap">Risk Map</option>
                            </select>
                        </div>

                        {/* File Input */}
                        <div className="mb-2">
                            <label htmlFor="file" className="mb-1 block text-black dark:text-white text-sm">
                                File (excel or csv)
                            </label>
                            <input
                                type="file"
                                id="file"
                                name="file"
                                onChange={(e) => setFile(e.target.files)}
                                className="border-gray w-full text-black rounded border-stroke bg-transparent py-1 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                            />
                        </div>
                        {/* Action Buttons */}
                        <div className="flex justify-end gap-2">
                            <button type="button" className="py-2 px-4 rounded text-white bg-danger" onClick={() => setShowBackendModal(false)}>Close</button>
                            <button type="submit" className="py-2 px-4 rounded text-white bg-primary">Upload</button>
                        </div>
                    </form>
                    {isUploading && (
                        <div className="mb-4">
                            <div className="flex justify-between mb-1">
                                <span className="text-sm font-medium">Uploading...</span>
                                <span className="text-sm font-medium">{uploadProgress}%</span>
                            </div>
                            <div className="w-full bg-[#e5e7eb] rounded-full h-2.5">
                                <div
                                    className="bg-[#2563eb] h-2.5 rounded-full"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {uploadSummary && (
                        <div className={`mt-4 p-2 border rounded text-sm ${uploadSummary.startsWith('Error:')
                            ? 'border-[#dc2626] bg-[#fee2e2] text-[#b91c1c]'
                            : 'border-[#16a34a] bg-[#dcfce7] text-[#166534]'
                            }`}>
                            <strong>{uploadSummary.startsWith('Error:') ? 'Error:' : 'Success:'}</strong> {uploadSummary}
                        </div>
                    )}
                </ModalLayout>
            )}

            {showFlashAlertsModal && <ModalLayout title="Add Flash Alert">
                <div>
                    <form className="mt-5">
                        {/* Title Input */}
                        <div className="mb-2">
                            <label htmlFor="title" className="mb-1 block text-black dark:text-white text-sm">
                                Title
                            </label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                placeholder="Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="border-gray w-full text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                            />
                        </div>

                        {/* Date and Location Inputs side by side */}
                        <div className="mb-2 flex space-x-4">
                            <div className="flex-1">
                                <label htmlFor="date" className="mb-1 block text-black dark:text-white text-sm">
                                    Date
                                </label>
                                <input
                                    type="date"
                                    id="date"
                                    name="date"
                                    value={dateField}
                                    onChange={(e) => setDateField(e.target.value)}
                                    className="border-gray w-full text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                                />
                            </div>
                            <div className="flex-1">
                                <label htmlFor="location" className="mb-1 block text-black dark:text-white text-sm">
                                    Location
                                </label>
                                <input
                                    type="text"
                                    id="location"
                                    name="location"
                                    placeholder="Region, District"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    className="border-gray w-full text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                                />
                            </div>
                        </div>

                        {/* Description (Rich Text Editor) */}
                        <div className="mb-2">
                            <label htmlFor="description" className="mb-1 block text-black dark:text-white text-sm">
                                Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                placeholder="Enter description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="border-gray w-full text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                                style={{ minHeight: '120px' }} // Set the minHeight to increase the size
                            />
                        </div>

                        {/* Images Input */}
                        <div className="mb-2">
                            <label htmlFor="file" className="mb-1 block text-black dark:text-white text-sm">
                                Images
                            </label>
                            <input
                                type="file"
                                multiple
                                id="images"
                                name="images"
                                onChange={(e) => setImages(e.target.files)}
                                className="border-gray w-full text-black rounded border-stroke bg-transparent py-1 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                            />
                        </div>

                        {/* Added By Input */}
                        <div className="mb-2">
                            <label htmlFor="addedBy" className="mb-1 block text-black dark:text-white text-sm">
                                Input By
                            </label>
                            <input
                                type="text"
                                id="added_by"
                                name="added_by"
                                placeholder="Your Name"
                                value={addedBy}
                                onChange={(e) => setAddedBy(e.target.value)}
                                className="border-gray w-full text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                            />
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-between">
                            {/* Left-aligned button */}
                            <button
                                type="button"
                                className="py-2 px-4 rounded text-white bg-danger"
                                onClick={() => setShowFlashAlertsModal(false)}
                            >
                                Close
                            </button>

                            {/* Right-aligned group */}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    className="py-2 px-4 rounded text-white bg-[#6B7280]"
                                    onClick={() => handleFlashAlertSubmit("Draft")}
                                >
                                    Save as Draft
                                </button>
                                <button
                                    type="button"
                                    className="py-2 px-4 rounded text-white bg-primary"
                                    onClick={() => handleFlashAlertSubmit("Active")}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </ModalLayout>}
            {showPartnersModal && (
                <ModalLayout title="Upload Partner Image">
                    <form
                        className="mt-5"
                        onSubmit={async (e) => {
                            e.preventDefault();
                            if (!file?.length || !partnerType) {
                                setUploadSummary("Error: Missing required fields.");
                                return;
                            }

                            setIsUploading(true);
                            setUploadProgress(0);
                            setUploadSummary("");

                            const filesArray = Array.from(file);
                            const totalFiles = filesArray.length;
                            let completed = 0;
                            let summaryMessages: string[] = [];

                            for (const f of filesArray) {
                                const result = await uploadPartnerImage(f, partnerType);
                                if (result.success) {
                                    summaryMessages.push(`✔️ ${f.name} uploaded successfully`);
                                } else {
                                    summaryMessages.push(`❌ ${f.name}: ${result.error}`);
                                }
                                completed += 1;
                                setUploadProgress(Math.round((completed / totalFiles) * 100));
                            }

                            setUploadSummary(summaryMessages.join("\n"));
                            setIsUploading(false);

                            // ✅ Automatically close the modal after successful upload
                            setTimeout(() => {
                                setShowPartnersModal(false);
                                setFile(null); // optional: reset file input
                                setUploadProgress(0); // optional: reset progress
                            }, 1500);
                        }}

                    >
                        {/* Partner Type Dropdown */}
                        <div className="mb-2">
                            <label htmlFor="partner-type" className="mb-1 block text-black dark:text-white text-sm">
                                Select Partner Type
                            </label>
                            <select
                                id="partner-type"
                                name="partner-type"
                                value={partnerType}
                                onChange={(e) => setPartnerType(e.target.value)}
                                className="border-gray w-full text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                            >
                                <option value="" disabled hidden>---Select Partner Type---</option>
                                <option value="donor_partners">Donor Partners</option>
                                <option value="data_partners">Data Partners</option>
                            </select>
                        </div>

                        {/* Image File Input */}
                        <div className="mb-2">
                            <label htmlFor="file" className="mb-1 block text-black dark:text-white text-sm">
                                Partner Logo/Image
                            </label>
                            <input
                                type="file"
                                accept="image/*"
                                id="file"
                                name="file"
                                multiple
                                onChange={(e) => setFile(e.target.files)}
                                className="border-gray w-full text-black rounded border-stroke bg-transparent py-1 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="py-2 px-4 rounded text-white bg-danger"
                                onClick={() => setShowPartnersModal(false)}
                            >
                                Close
                            </button>
                            <button
                                type="submit"
                                className="py-2 px-4 rounded text-white bg-primary"
                            >
                                Upload
                            </button>
                        </div>
                    </form>

                    {/* Upload Progress */}
                    {isUploading && (
                        <div className="mb-4">
                            <div className="flex justify-between mb-1">
                                <span className="text-sm font-medium">Uploading...</span>
                                <span className="text-sm font-medium">{uploadProgress}%</span>
                            </div>
                            <div className="w-full bg-[#e5e7eb] rounded-full h-2.5">
                                <div
                                    className="bg-[#2563eb] h-2.5 rounded-full"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Upload Summary Message */}
                    {uploadSummary && (
                        <div className={`mt-4 p-2 border rounded text-sm ${uploadSummary.startsWith('Error:')
                            ? 'border-[#dc2626] bg-[#fee2e2] text-[#b91c1c]'
                            : 'border-[#16a34a] bg-[#dcfce7] text-[#166534]'
                            }`}>
                            <strong>{uploadSummary.startsWith('Error:') ? 'Error:' : 'Success:'}</strong> {uploadSummary}
                        </div>
                    )}
                </ModalLayout>
            )}

            {showSnapshotModal && (
                <ModalLayout title="Update Humanitarian Snapshot">
                    <form className="mt-5 space-y-4" onSubmit={handleSnapshotSubmit}>
                        {/* Population in Need */}
                        <div>
                            <label htmlFor="populationInNeed" className="block text-sm text-black dark:text-white mb-1">
                                Population in Need
                            </label>
                            <input
                                type="number"
                                id="populationInNeed"
                                value={populationInNeed}
                                onChange={(e) => setPopulationInNeed(e.target.value)}
                                min={0}
                                required
                                placeholder="e.g. 120000"
                                className="w-full border-gray text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary dark:border-gray dark:bg-form-input"
                            />
                        </div>

                        {/* Population Target */}
                        <div>
                            <label htmlFor="populationTarget" className="block text-sm text-black dark:text-white mb-1">
                                Population Target
                            </label>
                            <input
                                type="number"
                                id="populationTarget"
                                value={populationTarget}
                                onChange={(e) => setPopulationTarget(e.target.value)}
                                min={0}
                                required
                                placeholder="e.g. 100000"
                                className="w-full border-gray text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary dark:border-gray dark:bg-form-input"
                            />
                        </div>

                        {/* Population Reached */}
                        <div>
                            <label htmlFor="populationReached" className="block text-sm text-black dark:text-white mb-1">
                                Population Reached
                            </label>
                            <input
                                type="number"
                                id="populationReached"
                                value={populationReached}
                                onChange={(e) => setPopulationReached(e.target.value)}
                                min={0}
                                required
                                placeholder="e.g. 85000"
                                className="w-full border-gray text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary dark:border-gray dark:bg-form-input"
                            />
                        </div>

                        {/* Funding Required */}
                        <div>
                            <label htmlFor="fundingRequired" className="block text-sm text-black dark:text-white mb-1">
                                Funding Required (USD)
                            </label>
                            <input
                                type="number"
                                id="fundingRequired"
                                value={fundingRequired}
                                onChange={(e) => setFundingRequired(e.target.value)}
                                min={0}
                                required
                                placeholder="e.g. 2000000"
                                className="w-full border-gray text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary dark:border-gray dark:bg-form-input"
                            />
                        </div>

                        {/* Funding Received */}
                        <div>
                            <label htmlFor="fundingReceived" className="block text-sm text-black dark:text-white mb-1">
                                Funding Received (USD)
                            </label>
                            <input
                                type="number"
                                id="fundingReceived"
                                value={fundingReceived}
                                onChange={(e) => setFundingReceived(e.target.value)}
                                min={0}
                                required
                                placeholder="e.g. 1500000"
                                className="w-full border-gray text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary dark:border-gray dark:bg-form-input"
                            />
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-2 pt-4">
                            <button
                                type="button"
                                className="py-2 px-4 rounded text-white bg-danger"
                                onClick={() => setShowSnapshotModal(false)}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="py-2 px-4 rounded text-white bg-primary flex items-center gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <svg
                                            className="animate-spin h-4 w-4 text-white"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            ></path>
                                        </svg>
                                        Submitting...
                                    </>
                                ) : (
                                    "Submit"
                                )}
                            </button>
                        </div>
                    </form>
                </ModalLayout>
            )}

            {/* Detail Modal */}
            {showDetailModal && (
                <div className="fixed inset-0 bg-[#1C1917]/60 backdrop-blur-[2px] flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden border border-[#E7E5E4] shadow-xl">

                        {/* Header */}
                        <div className="px-6 py-5 border-b border-[#E7E5E4] flex justify-between items-center">
                            <div>
                                <p className="text-[11px] font-semibold tracking-[0.12em] text-[#C2410C] uppercase mb-1">
                                    Record Detail
                                </p>
                                <h3 className="font-display text-lg font-semibold text-[#1C1917]">
                                    Approved Records
                                </h3>
                            </div>
                            <button
                                onClick={() => setShowDetailModal(false)}
                                className="text-[#A8A29E] hover:text-[#1C1917] hover:bg-[#FAFAF9] rounded-md p-1.5 transition-colors"
                                aria-label="Close"
                            >
                                <X size={18} strokeWidth={2} />
                            </button>
                        </div>

                        {/* Table */}
                        <div className="p-6 overflow-auto max-h-[70vh]">
                            <table className="w-full border-collapse text-sm text-[#1C1917]">
                                <thead>
                                    <tr className="border-b border-[#E7E5E4]">
                                        <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">ID</th>
                                        <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Date</th>
                                        <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Region</th>
                                        <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">District</th>
                                        <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Households</th>
                                        <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Individuals</th>
                                        <th className="p-3 text-left text-[11px] font-semibold tracking-wider text-[#78716C] uppercase">Reason</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#F0EEEC]">
                                    {modalRecords.map(record => (
                                        <tr key={record._id} className="hover:bg-[#FAFAF9] transition-colors">
                                            <td className="p-3 font-mono text-[13px] font-medium">{record._id}</td>
                                            <td className="p-3 font-mono text-[13px] text-[#57534E]">{record.report_date}</td>
                                            <td className="p-3">{record.region}</td>
                                            <td className="p-3">{record.district}</td>
                                            <td className="p-3 font-mono text-[13px]">{record.evicted_households}</td>
                                            <td className="p-3 font-mono text-[13px]">{record.evicted_individuals}</td>
                                            <td className="p-3 text-[13px] text-[#57534E]">{record.eviction_reasons}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-[#E7E5E4] flex justify-end gap-3">
                            {modalRecords.length >= 10 && (
                                <button
                                    onClick={() => generatePDF()}
                                    className="bg-[#C2410C] hover:bg-[#9A3412] text-white text-sm font-medium
                               px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
                                >
                                    <FileDown size={16} strokeWidth={2} />
                                    Download PDF
                                </button>
                            )}
                            <button
                                onClick={() => setShowDetailModal(false)}
                                className="text-sm font-medium text-[#57534E] border border-[#E7E5E4] rounded-md
                           px-4 py-2 hover:bg-[#FAFAF9] hover:border-[#D6D3D1] transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPanel;